// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import {
  all,
  call,
  delay,
  put,
  select,
  takeLatest,
  takeEvery,
  getContext,
} from "redux-saga/effects";
import { Store } from "@reduxjs/toolkit";
import { generateClient } from "aws-amplify/api";
import { Amplify } from "aws-amplify";

import { GraphQLResult, RootPath, MainPanel } from "types";
import {
  ICloudParameter,
  IVirtAssign,
  IDbAddress,
  ChargerConnectionType,
  IO_TREND_LIMIT,
  eRfidLimitPeriod,
  dbAddr2ParaAddr,
  paraAddr2DbAddr,
  makeVirtualSiteId,
  parseVirtualSiteId,
} from "shared/types/cloudtypes";
import {
  CustomDashboard,
  IInput,
  ILinxParameter,
  InputType,
  IOutput,
  OutputMode,
} from "shared/types/parameter";
import {
  ChargingMode as ApiChargingMode,
  ExecCommandMutation,
  ImportFileInput,
  ImportFileMutation,
  ListImportFilesQuery,
  ListImportFilesQueryVariables,
  ListRefusedRfidsQuery,
  ListRefusedRfidsQueryVariables,
  ListRfidsBasicQuery,
  ListUsersBasicQuery,
  ListUsersQueryVariables,
  ModifyChargerControlMutation,
  ModifyChargerMutation,
  ModifyConnectorInput,
  ModifyConnectorMutation,
  ModifyRfidMutation,
  ModifySiteMutation,
  ModifyTariffMutation,
  ModifyUserMutation,
  SetParameterInput,
  SetParameterMutation,
  TriggerChargerCommandMutation,
} from "../graphql/api";

import {
  listSites as listSitesGql,
  listUsers as listUsersGql,
  listCompanies as listCompaniesGql,
  getSiteParameter as getSiteParameterGql,
  listSiteParameterHistory as listSiteParameterHistoryGql,
  getDataBoundaries as getDataBoundariesGql,
  listTrendData as listTrendDataGql,
  listIoData as listIoDataGql,
  listAlarms as listAlarmsGql,
  listAlarmNotifications as listAlarmNotificationsGql,
  listReports as listReportsGql,
  listChargers,
  listConnectors,
  listRfids,
  listChargingSessions,
  triggerReport as triggerReportGql,
  listChargerStatusNotifications,
  checkRfid,
  listTariffs,
  getChargerControl,
  listRefusedRfids,
  listImportFiles,
} from "graphql/queries";
import {
  modifyAlarmNotification as modifyAlarmNotificationGql,
  modifyReport as modifyReportGql,
  modifyUser as modifyUserGql,
  modifySite as modifySiteGql,
  modifyCharger as modifyChargerGql,
  modifyConnector as modifyConnectorGql,
  modifyRfid as modifyRfidGql,
  modifyTariff as modifyTariffGql,
  newTrendData as newTrendDataGql,
  newAlarmData as newAlarmDataGql,
  execCommand as execCommandGql,
  triggerChargerCommand as triggerChargerCommandGql,
  modifyChargerControl as modifyChargerControlGql,
  importFile as importFileGql,
  setParameter as setParameterGql,
} from "graphql/mutations";
import {
  GetSiteParameterQuery,
  GetSiteParameterQueryVariables,
  ListSiteParameterHistoryQuery,
  ListSiteParameterHistoryQueryVariables,
  SiteParameterHistoryEntry,
  GetDataBoundariesQuery,
  GetDataBoundariesQueryVariables,
  ListSitesQuery,
  ListUsersQuery,
  ListCompaniesQuery,
  ListTrendDataQuery,
  ListTrendDataQueryVariables,
  ListIoDataQuery,
  ListIoDataQueryVariables,
  ListAlarmsQuery,
  ListAlarmsQueryVariables,
  ListAlarmNotificationsQuery,
  ListAlarmNotificationsQueryVariables,
  ModifyAlarmNotificationInput,
  ModifyAlarmNotificationMutation,
  ListReportsQuery,
  ListReportsQueryVariables,
  ModifyReportInput,
  ModifyReportMutation,
  ReportInterval,
  ReportType,
  ListChargersQuery,
  ListChargersQueryVariables,
  ListConnectorsQuery,
  ListConnectorsQueryVariables,
  ListRfidsQuery,
  ListRfidsQueryVariables,
  ListChargingSessionsQueryVariables,
  ListChargingSessionsQuery,
  ModifyUserInput,
  ModifySiteInput,
  ModifyChargerInput,
  ModifyRfidInput,
  NewTrendDataInput,
  NewTrendDataMutation,
  NewAlarmDataInput,
  NewAlarmDataMutation,
  TriggerReportQueryVariables,
  Aggregation,
  ExecCommandInput,
  Command,
  ReportFormat,
  DataSource,
  ListChargerStatusNotificationsQueryVariables,
  ListChargerStatusNotificationsQuery,
  GetChargerControlQueryVariables,
  GetChargerControlQuery,
  CheckRfidQueryVariables,
  CheckRfidQuery,
  ModifyTariffInput,
  ListTariffsQueryVariables,
  ListTariffsQuery,
  TriggerChargerCommandMutationVariables,
  ModifyChargerControlMutationVariables,
  ChargerCommand,
} from "graphql/api";
import amplifyconfig from "amplifyconfiguration.json";

import {
  ActionType,
  raiseAction,
  IAction,
  IActionTypeLogin,
  IActionCloudTrendDataRow,
  IActionChangeInput,
  IActionChangeOutput,
  IActionTypeUserlist,
  IActionTypeSitelist,
  IActionTypeChangeUser,
  IActionTypeChangeAlarmNotification,
  IActionAckAlarm,
  IActionChangeTrend,
  IActionTypeChangeSite,
  IActionTypeParameterLoad,
  IActionTypeParameterHistoryLoad,
  IActionTypeChargingSessionList,
  IActionTypeChangeReport,
  IActionTypeChangeReportsSettings,
  IActionChangeChargerSettings,
  IActionTypeRfidList,
  IActionChangeRfid,
  IActionCloudTrendInfo,
  IActionCloudIoDataRow,
  IActionTypeChangeTrendMonitorSettings,
  IActionOcppTrendInfo,
  IActionOcppCharger,
  IActionRfidSiteChargers,
  IActionTypeChargerStateChangeList,
  IActionChangeTariff,
  IActionOcppConnector,
  IActionNewChargingSessionData,
  IActionChargerCommand,
  IActionChargingPower,
  IActionCloudIoTrendData,
  IActionCommand,
  IActionCreateReport,
  IActionCloudTrendData,
  IActionTypeZoom,
  IActionImporterFileUpload,
  IActionSaveVirtualParameters,
} from "actions";
import { DeviceType, AlarmSource, IAlarmEntry } from "shared/types/globtypes";
import {
  AlarmNotification,
  IVirtSiteDefinition,
  ITrendReportFilter,
  NotificationPeriod,
  Report,
} from "shared/types/cloudtypes";
import { TimePeriod, ICloudTrendMember } from "types";
import { isSet } from "shared/utils/utils";
import {
  parseApiBool,
  parseApiFloat,
  parseApiInt,
  parseApiItemArray,
  parseApiString,
  base64decodeParameters,
} from "shared/utils/apiutils";
import {
  getGraphBoundaryDates,
  getGraphBoundaryDatesFromPeriod,
  isOcppFrontend,
  makeCommandSeqNr,
  getSite,
  getLocalDateTimeIso,
  downloadTextFile,
} from "guiutils";
import { IState } from "states/states";
import { Site, User, Company } from "states/sitestates";
import { IOcppChargingSession, IRefusedRfid, IRfid, ITariff } from "states/ocppstates";
import { IChargerStateEvent, ITrendDataRow } from "states/trendingstates";
import { ApiSagas, getAlarmState } from "./apisagas";
import { ProxyInterface } from "./proxyinterface";
import * as authSagas from "./authsagas";
import * as daSagas from "./dasagas";
import { ConnectorStatus, StopReason } from "model/connector";
import TrendWindowModel, { ITrendWindow } from "model/trendwindow";
import { CloudLiveDataHandler, IRegisterPushSelection } from "./cloudlivedatahandler";
import { createBrowserRouter } from "react-router-dom";
import { listRfidsBasic, listUsersBasic } from "graphql/customqueries";

// maximum number of rows we let a single listTrendData request produce. The server
// truncates at 20000 without telling us, so we keep a margin below that and split the
// request across tariff circuits if the requested period would exceed it.
const TREND_LOADING_LIMIT = 16000; // rows per request
const RFID_SITE_CHARGERS_BATCH = 5; // parallel requests when preloading chargers of linked sites

// zooming into an aggregated graph reloads finer data on the trend page only; e.g. the trend
// monitor graph buckets data by its own period
const isTrendZoomReloadPanel = (state: IState) => state.router.panel === MainPanel.ENERGY;

// live data timeout on server is 5 minutes, so we time out here shortly before this
const LIVE_DATA_REGISTRATION_TIMEOUT = 290 * 1000; // ms

// overwrite configuration
const EMBYT_GRAPHQL_ENDPOINT =
  import.meta.env.VITE_GRAPHQL_ENDPOINT ?? "https://enl-gql.embyt.com/graphql";

export function cloudTrend2LinxTrend(
  curData: ICloudTrendMember & {
    timestamp: number;
    siteId: number;
    chargerId?: number | null | undefined;
    tlId: number;
  },
  mainSite?: number,
) {
  // derive the tlId from original tlId and siteId, which is needed for virtual sites
  // if source is the main site, just take the tlId
  const tlId =
    !isSet(mainSite) || curData.siteId === mainSite ?
      curData.tlId
    : makeVirtualSiteId(curData.siteId, curData.tlId);
  const data: ITrendDataRow = {
    timestamp: curData.timestamp,
    tlId,
    regperiod: curData.regperiod,
    energy: curData.energy,
    power: curData.power,
    costs: curData.costs ?? undefined,
    tariff: curData.tariff ?? undefined,
    targetpower: curData.targetpower ?? undefined,
    soc: curData.soc ?? undefined,
  };
  const row: IActionCloudTrendDataRow = {
    siteId: curData.siteId,
    chargerId: curData.chargerId ?? undefined,
    data,
  };
  return row;
}

export class CloudSagas extends ApiSagas {
  private RFID_USER_POOL = "eu-central-1_XUY9ZZapR";
  private RFID_APP_CLIENT = "3rq5upkud0fprjeoir101inftn";

  private API = generateClient();

  private liveDataHandler: CloudLiveDataHandler | undefined;
  // transport to acquire controller live data + commands directly via the proxy
  private proxyInterface = new ProxyInterface();

  // main entry point to load sagas at startup
  public *initSagas() {
    yield* super.initSagas();

    yield takeEvery(ActionType[ActionType.ACT_VISIBILITY_CHANGE], this.visibilityChange.bind(this));

    yield* authSagas.initAuthSagas();
    yield takeEvery(ActionType[ActionType.ACT_CHANGE_USER_REQ], this.changeUser.bind(this));
    yield takeLatest(
      ActionType[ActionType.ACT_GET_ALARM_NOTIFICATIONS_REQ],
      this.getAlarmNotifications.bind(this),
    );
    yield takeEvery(
      ActionType[ActionType.ACT_CHANGE_ALARM_NOTIFICATION_REQ],
      this.changeAlarmNotification.bind(this),
    );

    yield takeEvery(
      ActionType[ActionType.ACT_CHANGE_SITESETTINGS_REQ],
      this.changeSiteSettings.bind(this),
    );
    yield takeLatest(ActionType[ActionType.ACT_GET_REPORTS_REQ], this.getReports.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_ZOOM_GRAPH], this.checkTrendZoom.bind(this));
    yield takeEvery(ActionType[ActionType.ACT_CHANGE_REPORT_REQ], this.changeReport.bind(this));
    yield takeEvery(
      ActionType[ActionType.ACT_SAVE_REPORTS_SETTINGS_REQ],
      this.changeReportsSettings.bind(this),
    );
    yield takeEvery(
      ActionType[ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_REQ],
      this.changeTrendMonitorSettings.bind(this),
    );
    yield takeLatest(ActionType[ActionType.ACT_CREATE_REPORT_REQ], this.createReport.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_GET_SITES_REQ], this.getSites.bind(this));
    yield takeLatest(
      ActionType[ActionType.ACT_GET_USERLIST_BASIC_REQ],
      this.getUserlistBasic.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_GET_USERLIST_EXT_REQ],
      this.getUserlistExt.bind(this),
    );
    yield takeLatest(ActionType[ActionType.ACT_GET_COMPANIES_REQ], this.getCompanies.bind(this));
    yield takeLatest(
      ActionType[ActionType.ACT_GET_PARAMETER_HISTORY_REQ],
      this.getParameterHistory.bind(this),
    );
    yield takeEvery(
      ActionType[ActionType.ACT_DOWNLOAD_PARAMETER_REQ],
      this.downloadParameterHistoryEntry.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_REGISTER_FOR_PUSH_DATA_REQ],
      (action: IAction<boolean>) => this.registerForPushData(action.payload),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_REGISTER_FOR_PUSH_DATA_SUCC],
      (action: IAction<boolean>) => this.updateProxyConnection(action.payload),
    );
    yield takeLatest([ActionType[ActionType.ACT_DISCONNECT_SITE]], this.unsubscribeData.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_GET_ALLDATA_REQ], this.getAllData.bind(this));
    yield takeLatest(
      ActionType[ActionType.ACT_GET_CONDITIONBLOCKSTATUS_REQ],
      this.getConditionBlockStatus.bind(this),
    );
    yield takeEvery(ActionType[ActionType.ACT_CHANGE_TREND_REQ], this.changeTrendData.bind(this));
    yield takeLatest(
      ActionType[ActionType.ACT_GET_OCPP_CHARGERS_REQ],
      this.getOcppChargers.bind(this),
    );
    yield takeEvery(
      ActionType[ActionType.ACT_SAVE_OCPP_CHARGERS_REQ],
      this.saveOcppCharger.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_GET_OCPP_RFIDS_BASIC_REQ],
      this.getOcppRfidsBasic.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_GET_OCPP_RFIDS_EXT_REQ],
      this.getOcppRfidsExt.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_GET_OCPP_REFUSED_RFIDS_REQ],
      this.getOcppRefusedRfids.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_CHECK_OCPP_RFIDS_REQ],
      this.checkOcppRfid.bind(this),
    );
    yield takeEvery(ActionType[ActionType.ACT_SAVE_OCPP_RFID_REQ], this.saveOcppRfid.bind(this));
    yield takeLatest(
      ActionType[ActionType.ACT_GET_OCPP_SITE_TARIFFS_REQ],
      this.getOcppTariffs.bind(this),
    );
    yield takeEvery(
      ActionType[ActionType.ACT_SAVE_OCPP_TARIFF_REQ],
      this.saveOcppTariff.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_GET_OCPP_CONNECTORS_REQ],
      this.getOcppConnectors.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_GET_PENDING_OCPP_SESSIONS_REQ],
      this.getPendingOcppSessions.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_GET_OCPP_SESSIONS_REQ],
      this.getOcppSessions.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_GET_OCPP_TRENDINFO_REQ],
      this.getOcppTrendInfo.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_SELECT_CHARGING_SESSION],
      this.getChargingSession.bind(this),
    );

    yield takeLatest(
      ActionType[ActionType.ACT_NEW_CHARGING_SESSION_DATA],
      this.newChargingSession.bind(this),
    );

    yield takeEvery(
      ActionType[ActionType.ACT_TRIGGER_CHARGER_CMD_REQ],
      this.triggerChargerCommand.bind(this),
    );
    yield takeEvery(
      ActionType[ActionType.ACT_SAVE_CHARGER_POWER_REQ],
      this.saveChargerPower.bind(this),
    );

    yield takeLatest(ActionType[ActionType.ACT_OPEN_SSH_TUNNEL_REQ], this.openSshTunnel.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_REBOOT_REQ], this.doReboot.bind(this));
    yield takeLatest(
      ActionType[ActionType.ACT_PERFORM_FILE_UPLOAD_REQ],
      this.performFileUpload.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_GET_FILEUPLOADS_REQ],
      this.getFileUploads.bind(this),
    );
    yield takeLatest(
      ActionType[ActionType.ACT_SAVE_VIRTUAL_PARAMETERS_REQ],
      this.saveVirtualParameters.bind(this),
    );
  }

  public async initApi(store: Store) {
    // overwrite the graphql endpoint URL
    amplifyconfig.aws_appsync_graphqlEndpoint = EMBYT_GRAPHQL_ENDPOINT;

    // setup communication interface to the cloud server
    // do we authenticate with RFID card?
    // routerState may not be available at this point, so we use the URL path
    if (window.location.pathname.startsWith(`/${RootPath.RFID}`)) {
      console.log("using RFID user pool");
      amplifyconfig.aws_user_pools_id = this.RFID_USER_POOL;
      amplifyconfig.aws_user_pools_web_client_id = this.RFID_APP_CLIENT;
    }

    // configure the graphql endpoint and default authentication
    Amplify.configure(amplifyconfig);

    // setup live data handler
    this.liveDataHandler = new CloudLiveDataHandler(this.API, store);
    // if the proxy connection drops unexpectedly, fall back to GraphQL push
    this.proxyInterface.onConnectionLost = () => {
      store.dispatch(raiseAction(ActionType.ACT_SET_LIVE_VIA_PROXY, false));
    };
  }

  public *doAutoLogin() {
    yield* authSagas.doAutoLogin();
  }

  protected *doLogin(action: IAction<IActionTypeLogin>) {
    yield* authSagas.doLogin(action);
  }

  protected *doLogout(action: IAction<undefined>) {
    yield* authSagas.doLogout(action);
  }

  protected *changeUser(action: IAction<IActionTypeChangeUser>) {
    // companyId is required, so we must have it
    if (!isSet(action.payload.companyId)) {
      yield put(raiseAction(ActionType.ACT_CHANGE_USER_FAIL, "Company is required"));
      return;
    }

    const args: ModifyUserInput = {
      id: isSet(action.payload.id) ? `${action.payload.id}` : undefined,
      email: action.payload.email?.trim(),
      companyId: action.payload.companyId,
      name: action.payload.name?.trim(),
      siteIds: action.payload.userSites,
      accessRights:
        isSet(action.payload.accessRights) ?
          JSON.stringify(action.payload.accessRights)
        : undefined,
      settings:
        isSet(action.payload.settings) ? JSON.stringify(action.payload.settings) : undefined,
      chargingControl:
        isSet(action.payload.chargerControl) ?
          JSON.stringify(action.payload.chargerControl)
        : undefined,
    };
    try {
      const result = (yield this.API?.graphql({
        query: modifyUserGql,
        variables: { data: args },
      })) as GraphQLResult<ModifyUserMutation> | undefined;

      // result may be undefined if this.API is undefined, which must not happen
      if (result?.data.modifyUser) {
        const shouldEditUser = !isSet(action.payload.id);
        // the result holds the user-id which was modifed or created!
        action.payload.id = parseInt(result.data.modifyUser);
        yield put(raiseAction(ActionType.ACT_CHANGE_USER_SUCC, action.payload));
        // if payload.id is undefined -> a new user was created!
        // navigate to user, but in editmode! (can we get the id somehow via graphQL response??!!)
        if (shouldEditUser) {
          // and switch to this user for editing!
          const router: ReturnType<typeof createBrowserRouter> = yield getContext("router");
          router.navigate(`../${MainPanel.USER_SETTINGS}?user=${result.data.modifyUser}`);
        }
      } else {
        yield put(raiseAction(ActionType.ACT_CHANGE_USER_FAIL));
      }
    } catch (err) {
      const errors = (err as any)?.["errors"];
      if (errors?.[0]?.message.includes("duplicate key value violates unique constraint")) {
        yield put(raiseAction(ActionType.ACT_CHANGE_USER_FAIL, "User exists already!"));
      } else {
        yield this.raiseHttpError(ActionType.ACT_CHANGE_USER_FAIL, err);
      }
    }

    // check for modification of rfid card properties, which is only possible for rfid users here
    const state = (yield select(this.getState)) as IState;
    const isRfidUser = !!state.site.user?.isRfidUser;
    if (isRfidUser && (action.payload.carName || action.payload.carBattery)) {
      const args: ModifyRfidInput = {
        // the rfid selection will be based on user ID
        carName: action.payload.carName,
        carBattery: action.payload.carBattery,
      };
      try {
        const result = (yield this.API?.graphql({
          query: modifyRfidGql,
          variables: { data: args },
        })) as GraphQLResult<ModifyRfidMutation> | undefined;
        if (result?.data.modifyRfid) {
          // reload changed data
          yield put(raiseAction(ActionType.ACT_GET_OCPP_RFIDS_BASIC_REQ));
        } else {
          yield this.raiseHttpError(ActionType.ACT_SAVE_OCPP_RFID_FAIL, "RFID modification failed");
        }
      } catch (error) {
        yield this.raiseHttpError(ActionType.ACT_SAVE_OCPP_RFID_FAIL, error);
      }
    }
  }

  protected *changeSiteSettings(action: IAction<IActionTypeChangeSite>) {
    const state = (yield select(this.getState)) as IState;
    // take care: we must only use the direct (alarm) settings from the action payload
    // the report and trend monitor settings must be taken from the state space
    // as the might have been modified in the meanwhile
    const cloudSettings = action.payload.cloudSettings ?? {};
    const stateCloudSettings = state.site.siteList?.find(
      (curItem) => curItem.id === action.payload.id,
    )?.cloudSettings;
    cloudSettings.trendMonitor = stateCloudSettings?.trendMonitor;
    const args: ModifySiteInput = {
      id: `${action.payload.id}`,
      companyId: action.payload.companyId,
      cloudSettings: JSON.stringify(cloudSettings),
    };
    try {
      const result = (yield this.API?.graphql({
        query: modifySiteGql,
        variables: { data: args },
      })) as GraphQLResult<ModifySiteMutation> | undefined;
      if (result?.data.modifySite) {
        yield put(raiseAction(ActionType.ACT_CHANGE_SITESETTINGS_SUCC, action.payload));
      } else {
        yield put(raiseAction(ActionType.ACT_CHANGE_SITESETTINGS_FAIL));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_CHANGE_SITESETTINGS_FAIL, error);
    }
  }

  // the data reports of the given user, or of the logged-in user without a user id
  protected *getReports(action: IAction<number | undefined>) {
    const args: ListReportsQueryVariables = { userId: action.payload };
    try {
      const result = (yield this.API?.graphql({
        query: listReportsGql,
        variables: args,
      })) as GraphQLResult<ListReportsQuery> | undefined;
      const reportList = (result?.data?.listReports ?? []).map((curRow) => {
        const report: Report = {
          id: parseApiInt(curRow?.id),
          userId: parseApiInt(curRow?.userId),
          siteId: isSet(curRow?.siteId) ? parseApiInt(curRow.siteId) : undefined,
          companyId: isSet(curRow?.companyId) ? parseApiInt(curRow.companyId) : undefined,
          title: parseApiString(curRow?.title),
          isActive: curRow?.isActive === true,
          // the api and the database enums hold the same values, but are separate types
          type: curRow?.type as string as Report["type"],
          content: JSON.parse(curRow?.content ? curRow.content : "{}"),
          exportInterval: curRow?.exportInterval as string as Report["exportInterval"],
          recipients: curRow?.recipients ?? undefined,
          fileFormat: (curRow?.fileFormat ?? undefined) as Report["fileFormat"],
          csvDelimiter: (curRow?.csvDelimiter ?? undefined) as Report["csvDelimiter"],
          hideSumRow: curRow?.hideSumRow === true,
        };
        return report;
      });
      yield put(raiseAction(ActionType.ACT_GET_REPORTS_SUCC, reportList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_REPORTS_FAIL, error);
    }
  }

  protected *changeReport(action: IAction<IActionTypeChangeReport>) {
    const report = action.payload.report;
    // the api deletes a report if it is modified without isActive
    const args: ModifyReportInput =
      action.payload.isDelete ?
        { id: `${report.id}` }
      : {
          id: isSet(report.id) ? `${report.id}` : undefined,
          userId: report.userId,
          siteId: report.siteId,
          companyId: report.companyId,
          title: report.title.trim(),
          isActive: report.isActive,
          // the api and the database enums hold the same values, but are separate types
          type: report.type as string as ReportType,
          content: JSON.stringify(report.content),
          exportInterval: report.exportInterval as string as ReportInterval,
          // null clears a previously set recipient list, undefined would keep it
          recipients: report.recipients?.trim() || null,
          fileFormat: report.fileFormat,
          csvDelimiter: report.csvDelimiter,
          hideSumRow: report.hideSumRow,
        };
    try {
      const result = (yield this.API?.graphql({
        query: modifyReportGql,
        variables: { data: args },
      })) as GraphQLResult<ModifyReportMutation> | undefined;

      if (result?.data.modifyReport) {
        yield put(raiseAction(ActionType.ACT_CHANGE_REPORT_SUCC));
        // reload the list to also get the changes applied by the api
        yield put(raiseAction(ActionType.ACT_GET_REPORTS_REQ, report.userId));
      } else {
        yield put(raiseAction(ActionType.ACT_CHANGE_REPORT_FAIL));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_CHANGE_REPORT_FAIL, error);
    }
  }

  protected *changeReportsSettings(action: IAction<IActionTypeChangeReportsSettings>) {
    const state = (yield select(this.getState)) as IState;
    const sites = state.site.siteList?.filter((curItem) => curItem.id === action.payload.siteId);
    if (sites?.length !== 1) {
      yield put(raiseAction(ActionType.ACT_SAVE_REPORT_SETTINGS_FAIL, "site settings not found"));
      return;
    }
    const cloudSettings = { ...sites[0].cloudSettings };
    // modified reports-Settings
    cloudSettings.reportsSettings = action.payload.reportsSettings;

    const args: ModifySiteInput = {
      id: `${action.payload.siteId}`,
      cloudSettings: JSON.stringify(cloudSettings),
    };
    try {
      const result = (yield this.API?.graphql({
        query: modifySiteGql,
        variables: { data: args },
      })) as GraphQLResult<ModifySiteMutation> | undefined;
      if (result?.data.modifySite) {
        yield put(
          raiseAction(ActionType.ACT_SAVE_REPORT_SETTINGS_SUCC, {
            id: action.payload.siteId,
            cloudSettings,
          }),
        );
      } else {
        yield put(raiseAction(ActionType.ACT_SAVE_REPORT_SETTINGS_FAIL));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_SAVE_REPORT_SETTINGS_FAIL, error);
    }
  }

  protected *changeTrendMonitorSettings(action: IAction<IActionTypeChangeTrendMonitorSettings>) {
    const state = (yield select(this.getState)) as IState;
    const sites = state.site.siteList?.filter((curItem) => curItem.id === action.payload.siteId);
    if (sites?.length !== 1) {
      yield put(
        raiseAction(ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_FAIL, "site settings not found"),
      );
      return;
    }
    const cloudSettings = { ...sites[0].cloudSettings };
    if (!Array.isArray(cloudSettings.trendMonitor)) {
      cloudSettings.trendMonitor = [];
    } else {
      // clone reports array to avoid modifying source data structure
      cloudSettings.trendMonitor = [...cloudSettings.trendMonitor];
    }
    // new trend monitor, deleted or modified item?
    if (action.payload.trendMonitorId === undefined) {
      // new item
      cloudSettings.trendMonitor.push(action.payload.trendMonitorSettings!);
    } else if (action.payload.trendMonitorSettings === undefined) {
      // deleted trend monitor, and reindex remaining items
      cloudSettings.trendMonitor.splice(action.payload.trendMonitorId, 1);
    } else {
      // modified item
      cloudSettings.trendMonitor[action.payload.trendMonitorId] =
        action.payload.trendMonitorSettings;
    }
    const args: ModifySiteInput = {
      id: `${action.payload.siteId}`,
      cloudSettings: JSON.stringify(cloudSettings),
    };
    try {
      const result = (yield this.API?.graphql({
        query: modifySiteGql,
        variables: { data: args },
      })) as GraphQLResult<ModifySiteMutation> | undefined;
      if (result?.data.modifySite) {
        yield put(
          raiseAction(ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_SUCC, {
            id: action.payload.siteId,
            cloudSettings,
          }),
        );
      } else {
        yield put(raiseAction(ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_FAIL));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_FAIL, error);
    }
  }

  protected *createReport(action: IAction<IActionCreateReport>) {
    const reportId = action.payload.reportId;
    const state = (yield select(this.getState)) as IState;

    // construct customization filter
    const filter: ITrendReportFilter[] = [];
    if (state.gui.reportOptions.startDate) {
      // local state in ms; API in s
      filter.push({ type: "begin", ref: state.gui.reportOptions.startDate / 1000 });
    }
    if (state.gui.reportOptions.endDate) {
      filter.push({ type: "end", ref: state.gui.reportOptions.endDate / 1000 });
    }

    // the report id alone identifies the report and its site or company scope;
    // the site is only passed for the legacy reports of the site cloud settings
    const args: TriggerReportQueryVariables = {
      reportId,
      outputFormat: action.payload.previewOnly ? ReportFormat.data : ReportFormat.link,
      customization:
        filter.length > 0 ?
          JSON.stringify({
            filter,
          })
        : undefined,
    };
    try {
      yield this.API?.graphql({
        query: triggerReportGql,
        variables: args,
      }); //as GraphQLResult<TriggerReportMutation>;
      yield put(
        raiseAction(
          action.payload.previewOnly ?
            ActionType.ACT_CREATE_REPORT_DATA_SUCC
          : ActionType.ACT_CREATE_REPORT_SUCC,
          reportId,
        ),
      );
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_CREATE_REPORT_FAIL, error);
    }
  }

  protected *getSites(action: IAction<undefined>) {
    try {
      const result = (yield this.API?.graphql({
        query: listSitesGql,
      })) as GraphQLResult<ListSitesQuery> | undefined;
      const siteList = (result?.data?.listSites ?? []).map((curRow) => {
        const site: Site = {
          id: parseApiInt(curRow!.id),
          name: parseApiString(curRow?.name),
          serial: parseApiString(curRow?.serial),
          companyId: curRow!.companyId,
          longitude: parseApiFloat(curRow?.longitude),
          latitude: parseApiFloat(curRow?.latitude),
          deviceType: (curRow?.deviceType ?? DeviceType.LIOB_586) as DeviceType,
          deviceSw: parseApiString(curRow?.deviceSw),
          tzOffset: isSet(curRow?.tzOffset) ? parseApiInt(curRow?.tzOffset) : undefined,
          ipAddresses: isSet(curRow?.ipAddresses) ? parseApiString(curRow.ipAddresses) : undefined,
          cloudSettings: JSON.parse(curRow?.cloudSettings ? curRow?.cloudSettings : "{}"),
          unackAlarms: isSet(curRow?.unackAlarms) ? parseApiInt(curRow?.unackAlarms) : undefined,
          unclearedAlarms:
            isSet(curRow?.unclearedAlarms) ? parseApiInt(curRow?.unclearedAlarms) : undefined,
        };
        return site;
      }) as IActionTypeSitelist;
      yield put(raiseAction(ActionType.ACT_GET_SITES_SUCC, siteList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_SITES_FAIL, error);
    }
  }

  protected *getUserlistBasic(action: IAction<undefined>) {
    try {
      const result = (yield this.API?.graphql({
        query: listUsersBasic,
      })) as GraphQLResult<ListUsersBasicQuery> | undefined;
      const userList = (result?.data?.listUsers ?? []).map((curRow) => {
        const user: User = {
          id: parseApiInt(curRow?.id),
          secId: parseApiString(curRow?.secondaryId),
          email: parseApiString(curRow?.email),
          name: parseApiString(curRow?.name),
          companyId: parseApiInt(curRow?.companyId),
          accessRights: JSON.parse(curRow?.accessRights ? curRow.accessRights : "{}"),
        };
        return user;
      }) as IActionTypeUserlist;
      yield put(raiseAction(ActionType.ACT_GET_USERLIST_BASIC_SUCC, userList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_USERLIST_BASIC_FAIL, error);
    }
  }

  protected *getUserlistExt(action: IAction<number | undefined>) {
    const userIds = isSet(action.payload) ? [action.payload] : [];
    const args: ListUsersQueryVariables = { userIds };
    try {
      const result = (yield this.API?.graphql({
        query: listUsersGql,
        variables: args,
      })) as GraphQLResult<ListUsersQuery> | undefined;
      const userList = (result?.data?.listUsers ?? []).map((curRow) => {
        const user: User = {
          id: parseApiInt(curRow?.id),
          secId: parseApiString(curRow?.secondaryId),
          email: parseApiString(curRow?.email),
          name: parseApiString(curRow?.name),
          companyId: parseApiInt(curRow?.companyId),
          accessRights: JSON.parse(curRow?.accessRights ? curRow.accessRights : "{}"),
          settings: JSON.parse(curRow?.settings ? curRow.settings : "{}"),
          siteIds: parseApiItemArray(curRow?.siteIds, parseApiInt),
          chargingControl: curRow?.chargingControl ? JSON.parse(curRow.chargingControl) : undefined,
        };
        return user;
      }) as IActionTypeUserlist;
      yield put(raiseAction(ActionType.ACT_GET_USERLIST_EXT_SUCC, userList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_USERLIST_EXT_FAIL, error);
    }
  }

  // the alarm notifications of the given user, or of the logged-in user without a user id
  protected *getAlarmNotifications(action: IAction<number | undefined>) {
    const args: ListAlarmNotificationsQueryVariables = { userId: action.payload };
    try {
      const result = (yield this.API?.graphql({
        query: listAlarmNotificationsGql,
        variables: args,
      })) as GraphQLResult<ListAlarmNotificationsQuery> | undefined;
      const notificationList = (result?.data?.listAlarmNotifications ?? []).map((curRow) => {
        const notification: AlarmNotification = {
          id: parseApiInt(curRow?.id),
          userId: parseApiInt(curRow?.userId),
          name: curRow?.name ?? undefined,
          isActive: curRow?.isActive === true,
          // the api and the database enum hold the same values, but are separate types
          period: curRow?.period as string as NotificationPeriod,
          sources: (curRow?.sources ?? []) as AlarmSource[],
          recipients: curRow?.recipients ?? undefined,
          includeCleared: curRow?.includeCleared === true,
          allSites: curRow?.allSites === true,
          siteIds: parseApiItemArray(curRow?.siteIds, parseApiInt),
        };
        return notification;
      });
      yield put(raiseAction(ActionType.ACT_GET_ALARM_NOTIFICATIONS_SUCC, notificationList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_ALARM_NOTIFICATIONS_FAIL, error);
    }
  }

  protected *changeAlarmNotification(action: IAction<IActionTypeChangeAlarmNotification>) {
    const notification = action.payload.notification;
    // the api deletes a notification if it is modified without isActive
    const args: ModifyAlarmNotificationInput =
      action.payload.isDelete ?
        { id: `${notification.id}` }
      : {
          id: isSet(notification.id) ? `${notification.id}` : undefined,
          userId: notification.userId,
          name: notification.name?.trim(),
          isActive: notification.isActive,
          period: notification.period as string as ModifyAlarmNotificationInput["period"],
          sources: notification.sources,
          recipients: notification.recipients?.trim(),
          includeCleared: notification.includeCleared,
          allSites: notification.allSites,
          siteIds: notification.siteIds,
        };
    try {
      const result = (yield this.API?.graphql({
        query: modifyAlarmNotificationGql,
        variables: { data: args },
      })) as GraphQLResult<ModifyAlarmNotificationMutation> | undefined;

      if (result?.data.modifyAlarmNotification) {
        yield put(raiseAction(ActionType.ACT_CHANGE_ALARM_NOTIFICATION_SUCC));
        // reload the list to also get the changes applied by the api, e.g. denied sites
        yield put(raiseAction(ActionType.ACT_GET_ALARM_NOTIFICATIONS_REQ, notification.userId));
      } else {
        yield put(raiseAction(ActionType.ACT_CHANGE_ALARM_NOTIFICATION_FAIL));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_CHANGE_ALARM_NOTIFICATION_FAIL, error);
    }
  }

  protected *getCompanies(action: IAction<undefined>) {
    try {
      const result = (yield this.API?.graphql({
        query: listCompaniesGql,
      })) as GraphQLResult<ListCompaniesQuery> | undefined;
      const companyList = (result?.data?.listCompanies ?? []).map((curRow) => {
        const company: Company = {
          id: parseApiInt(curRow?.id),
          name: parseApiString(curRow?.name),
          parentId: curRow?.parentId ?? null,
        };
        return company;
      });
      yield put(raiseAction(ActionType.ACT_GET_COMPANIES_SUCC, companyList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_COMPANIES_FAIL, error);
    }
  }

  protected *getParameters(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: GetSiteParameterQueryVariables = { siteId: state.cached.selectedSite! };
    try {
      let parameter: ICloudParameter | undefined | null;
      let parameterVirtual: IVirtSiteDefinition | undefined | null;
      let timestamp: number | undefined;
      const result = (yield this.API?.graphql({
        query: getSiteParameterGql,
        variables: args,
      })) as GraphQLResult<GetSiteParameterQuery> | undefined;
      // convert json string to object
      if (result?.data?.getSiteParameter) {
        const siteParameter = result.data.getSiteParameter;
        if (siteParameter.parameter) {
          parameter = JSON.parse(siteParameter.parameter) as ICloudParameter | null;
        }
        if (siteParameter.parameterVirtual) {
          parameterVirtual = JSON.parse(
            siteParameter.parameterVirtual,
          ) as IVirtSiteDefinition | null;
        }
        timestamp = siteParameter.timestamp ?? undefined;
      }
      yield put(
        raiseAction(ActionType.ACT_GET_PARAMETER_SUCC, {
          parameter: parameter ?? undefined,
          parameterVirtual: parameterVirtual ?? undefined,
          timestamp,
        } as IActionTypeParameterLoad),
      );
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_PARAMETER_FAIL, error);
    }
  }

  protected *getParameterHistory(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: ListSiteParameterHistoryQueryVariables = { siteId: state.cached.selectedSite! };
    try {
      const result = (yield this.API?.graphql({
        query: listSiteParameterHistoryGql,
        variables: args,
      })) as GraphQLResult<ListSiteParameterHistoryQuery> | undefined;
      const history = (result?.data?.listSiteParameterHistory ?? []) as SiteParameterHistoryEntry[];
      yield put(
        raiseAction(ActionType.ACT_GET_PARAMETER_HISTORY_SUCC, {
          history,
        } as IActionTypeParameterHistoryLoad),
      );
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_PARAMETER_HISTORY_FAIL, error);
    }
  }

  protected *downloadParameterHistoryEntry(action: IAction<number>) {
    const state = (yield select(this.getState)) as IState;
    const timestamp = action.payload;
    const args: GetSiteParameterQueryVariables = {
      siteId: state.cached.selectedSite!,
      timestamp,
    };
    try {
      const result = (yield this.API?.graphql({
        query: getSiteParameterGql,
        variables: args,
      })) as GraphQLResult<GetSiteParameterQuery> | undefined;
      const parameterJs = result?.data?.getSiteParameter?.parameterJs;
      if (!parameterJs) {
        throw new Error(`no parameter data found for timestamp ${timestamp}`);
      }
      const siteName = getSite(state)?.name ?? "site";
      // build the plain-JSON content of an .epara file (as read/written by the enplan PC tool)
      // from the per-group base64-encoded parameterJs blob returned by the cloud API
      const { parameter } = base64decodeParameters(parameterJs, undefined);
      if (!parameter) {
        throw new Error(`no parameter data found for timestamp ${timestamp}`);
      }
      const eparaContent: ILinxParameter = { ...(parameter as ILinxParameter), siteName };
      const content = JSON.stringify(eparaContent, null, 2);
      const filenameTimestamp = getLocalDateTimeIso(timestamp * 1000).replace(/[:T]/g, "-");
      downloadTextFile(content, `${siteName}_${filenameTimestamp}.epara`);
    } catch (error) {
      window.alert("Could not download the parameter file.");
      yield this.raiseHttpError(ActionType.ACT_DOWNLOAD_PARAMETER_FAIL, error);
    }
  }

  protected *getAlarms(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: ListAlarmsQueryVariables = {
      siteId: state.cached.selectedSite!,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listAlarmsGql,
        variables: args,
      })) as GraphQLResult<ListAlarmsQuery> | undefined;
      // convert json string to object
      if (result?.data?.listAlarms) {
        const data: IAlarmEntry[] = result.data.listAlarms.map((curData) => {
          const row: IAlarmEntry = {
            source: curData!.source as AlarmSource,
            alarmTimestamp: curData!.alarmTimestamp,
            clearTimestamp: curData!.clearTimestamp ?? undefined,
            value: curData!.value,
            state: getAlarmState(curData!.isActive, Boolean(curData!.ackTimestamp)),
            description: curData!.description ?? undefined,
          };
          return row;
        });
        yield put(raiseAction(ActionType.ACT_GET_ALARMS_SUCC, data));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_ALARMS_FAIL, error);
    }
  }

  protected *getTrendInfo(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: GetDataBoundariesQueryVariables = {
      siteId: state.cached.selectedSite!,
      dataSource: DataSource.Trend,
    };
    try {
      let result = (yield this.API?.graphql({
        query: getDataBoundariesGql,
        variables: args,
      })) as GraphQLResult<GetDataBoundariesQuery> | undefined;
      const minTrendTimestamp = result?.data?.getDataBoundaries?.[0] ?? undefined; // null -> undefined
      const maxTrendTimestamp = result?.data?.getDataBoundaries?.[1] ?? undefined; // null -> undefined

      args.dataSource = DataSource.Input;
      result = (yield this.API?.graphql({
        query: getDataBoundariesGql,
        variables: args,
      })) as GraphQLResult<GetDataBoundariesQuery> | undefined;
      const minInputTimestamp = result?.data?.getDataBoundaries?.[0] ?? undefined; // null -> undefined
      const maxInputTimestamp = result?.data?.getDataBoundaries?.[1] ?? undefined; // null -> undefined

      args.dataSource = DataSource.Output;
      result = (yield this.API?.graphql({
        query: getDataBoundariesGql,
        variables: args,
      })) as GraphQLResult<GetDataBoundariesQuery> | undefined;
      const minOutputTimestamp = result?.data?.getDataBoundaries?.[0] ?? undefined; // null -> undefined
      const maxOutputTimestamp = result?.data?.getDataBoundaries?.[1] ?? undefined; // null -> undefined

      const payload: IActionCloudTrendInfo = {
        minTrendTimestamp,
        maxTrendTimestamp,
        minInputTimestamp,
        maxInputTimestamp,
        minOutputTimestamp,
        maxOutputTimestamp,
      };
      yield put(raiseAction(ActionType.ACT_GET_TRENDINFO_SUCC, payload));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_TRENDINFO_FAIL, error);
    }
  }

  protected *getOcppTrendInfo(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: GetDataBoundariesQueryVariables = {
      siteId: state.cached.selectedSite,
      rfid: state.site.user?.isRfidUser ? state.site.user.secId : undefined,
      dataSource: DataSource.Charging,
    };
    try {
      const result = (yield this.API?.graphql({
        query: getDataBoundariesGql,
        variables: args,
      })) as GraphQLResult<GetDataBoundariesQuery> | undefined;
      // getDataBoundaries might be null if not trend data is present at all (e.g. for chargers)
      const minTrendTimestamp = result?.data?.getDataBoundaries?.[0] ?? undefined; // null -> undefined
      const maxTrendTimestamp = result?.data?.getDataBoundaries?.[1] ?? undefined; // null -> undefined
      const payload: IActionOcppTrendInfo = {
        minTrendTimestamp,
        maxTrendTimestamp,
      };
      yield put(raiseAction(ActionType.ACT_GET_OCPP_TRENDINFO_SUCC, payload));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_OCPP_TRENDINFO_FAIL, error);
    }
  }

  protected *getTrend(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const site = state.site.siteList?.find((curSite) => curSite.id === state.cached.selectedSite);
    const customDashboardSettings =
      (site?.cloudSettings?.customDashboard as CustomDashboard | undefined) ??
      state.para.parameter?.general?.customSettings?.customDashboard;

    // determine time range; we need special handling for custom dashboard view
    const isCustomDashboard =
      isSet(state.router.panel) &&
      [MainPanel.CUSTOM_DASHBOARD, MainPanel.PUBLIC_DISPLAY].includes(state.router.panel);
    const now = Math.round(Date.now() / 1000);

    let trendWindow: ITrendWindow;
    if (isCustomDashboard) {
      const graphDates = getGraphBoundaryDates(
        now - (customDashboardSettings?.timerange ?? 7) * 24 * 3600,
        now,
        state.site.tzOffset,
        TimePeriod.CUSTOM,
      );
      const dashboardAggregation =
        isSet(customDashboardSettings?.graph?.aggregation) ?
          (customDashboardSettings.graph.aggregation as Aggregation)
        : TrendWindowModel.getAggregation(
            TimePeriod.CUSTOM,
            graphDates.startDate,
            graphDates.endDate,
            TrendWindowModel.getAggregationBoundary(state),
          );
      trendWindow = {
        startDate: graphDates.startDate,
        endDate: graphDates.endDate,
        aggregation: dashboardAggregation,
        // the dashboard shows the last days up to now
        isLive: dashboardAggregation === Aggregation.NONE,
      };
    } else {
      trendWindow = TrendWindowModel.fromPeriod(state, isTrendZoomReloadPanel(state));
    }
    const { startDate, endDate, aggregation } = trendWindow;
    // do we need to perform multiple requests due to high data count?
    const secondsPerRow =
      aggregation === Aggregation.HOUR ? 3600
      : aggregation === Aggregation.DAY ? 24 * 3600
      : aggregation === Aggregation.MONTH ?
        28 * 24 * 3600 // short month: rather too many rows
      : Math.min(
          // fastest tariff circuit determines the row count
          ...(state.para.parameter?.tariffline ?? []).map((curItem) => curItem.regulationPeriod),
        );
    const duration = (endDate ?? now) - (startDate ?? state.trending.minTrendTimestamp ?? now);
    const rowsPerTariffline = Math.max(duration / secondsPerRow, 1);
    const chunkSize = Math.max(Math.floor(TREND_LOADING_LIMIT / rowsPerTariffline), 1);

    const tlList =
      (state.para.parameter?.tariffline.length ?? 0) > chunkSize ?
        state.para.parameter?.tariffline.map((curItem, curIndex) => curItem.id ?? curIndex)
      : undefined;

    const args: ListTrendDataQueryVariables = {
      siteId: state.cached.selectedSite!,
      startDate,
      endDate,
      aggregation,
    };
    let pos = 0;
    try {
      do {
        args.tlId = tlList ? tlList.slice(pos, pos + chunkSize) : null;
        const result = (yield this.API?.graphql({
          query: listTrendDataGql,
          variables: args,
        })) as GraphQLResult<ListTrendDataQuery> | undefined;
        const data =
          result?.data?.listTrendData?.map((curData) => {
            return cloudTrend2LinxTrend(curData!, state.cached.selectedSite!);
          }) ?? [];
        pos += chunkSize;
        const progress = Math.min(pos / (tlList?.length ?? 1), 1);
        const payload: IActionCloudTrendData = {
          data,
          progress,
          trendWindow,
        };
        yield put(raiseAction(ActionType.ACT_GET_CLOUDTREND_SUCC, payload));
      } while (pos < (tlList?.length ?? 1));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_TREND_FAIL, error);
    }
  }

  // reload trend data if a graph zoom requires finer aggregated or not yet loaded data
  private *checkTrendZoom(action: IAction<IActionTypeZoom>) {
    if (action.payload.identifier !== "trendPeriod") return;
    // debounce wheel zooming: takeLatest cancels us on the next zoom action
    yield delay(500);
    const state = (yield select(this.getState)) as IState;
    if (!isTrendZoomReloadPanel(state)) return;
    const wanted = TrendWindowModel.fromPeriod(state, true);
    if (!TrendWindowModel.covers(state.trending.loadedTrendWindow, wanted)) {
      yield put(raiseAction(ActionType.ACT_UPDATE_TREND));
    }
  }

  private getIoParameterIndex(
    parameter: IInput[] | IOutput[],
    virtualSite: IVirtAssign[] | undefined,
    sourceSite: number | undefined,
    addr: IDbAddress,
    isOutput: boolean,
  ) {
    let index: number | undefined;
    // first, look in virtual sites as this is clearly identified
    const inAddr = dbAddr2ParaAddr(addr, isOutput);
    virtualSite?.forEach((curItem) => {
      if (curItem.sourceSite === sourceSite) {
        const parameterItem = parameter[curItem.destIndex!] as IInput | IOutput | undefined;
        if (
          parameterItem?.bus === inAddr.bus &&
          parameterItem?.module === inAddr.module &&
          parameterItem.channel === inAddr.channel
        ) {
          index = curItem.destIndex;
        }
      }
    });
    // did we find a matching virtual site?
    if (index === undefined) {
      // no virtual site found
      parameter.some((curItem, curIndex) => {
        if (
          curItem.bus === inAddr.bus &&
          curItem.module === inAddr.module &&
          curItem.channel === inAddr.channel
        ) {
          // ensure that this is no virtual item
          if (!virtualSite?.some((curAssign) => curAssign.destIndex === curIndex)) {
            index = curIndex;
            return true;
          }
        }
        return false;
      });
    }
    if (index === undefined) {
      console.error("cannot find io index", inAddr);
    }
    return index;
  }

  private cloudIoDataToActionData(
    graphqlData: ListIoDataQuery,
    parameter: ICloudParameter,
    isOutput: boolean,
  ) {
    const data: IActionCloudIoDataRow[] = [];
    const virtParameter =
      isOutput ?
        (parameter as ICloudParameter)?.virtualSite?.output
      : (parameter as ICloudParameter)?.virtualSite?.input;
    graphqlData.listIoData?.forEach((curData) => {
      const index = this.getIoParameterIndex(
        (isOutput ? parameter?.output : parameter?.input) ?? [],
        virtParameter,
        curData!.siteId,
        curData!,
        isOutput,
      );
      if (isSet(index)) {
        const row: IActionCloudIoDataRow = {
          siteId: curData!.siteId,
          // IoData.timestamp is a Float and embyt devices record sub-second timestamps;
          // the trend stores are keyed by whole seconds, so round on the way in
          timestamp: Math.round(curData!.timestamp),
          source: isOutput ? "output" : "input",
          index,
          value: curData!.value,
          count: curData!.count,
        };
        data.push(row);
      }
    });
    return data;
  }

  protected *getIoTrend(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const { graphStart, graphEnd } = getGraphBoundaryDatesFromPeriod(
      state.gui.ioPeriod,
      state.site.tzOffset,
    );
    const aggregation = TrendWindowModel.getIoAggregation(state);
    // helper function
    const isDigital = (item: IInput | IOutput | undefined, isOutput: boolean) =>
      isSet(item) &&
      (isOutput ?
        (item as IOutput).mode !== OutputMode.ANALOG
      : (item as IInput).type !== InputType.ANALOG);

    const args: ListIoDataQueryVariables = {
      siteId: state.cached.selectedSite!,
      dataSource: DataSource.Input,
      startDate: graphStart,
      endDate: graphEnd,
      aggregation,
      aggregationType:
        aggregation === Aggregation.NONE ? undefined : state.gui.selectedIoAggregationType,
    };

    const sources = [DataSource.Input, DataSource.Output];
    const ios: (IInput[] | IOutput[])[] = [
      state.para.parameter?.input.filter((input) => input.webActive === true) ?? [],
      state.para.parameter?.output.filter((output) => output.webActive === true) ?? [],
    ];
    let data: IActionCloudIoDataRow[] = [];
    let digitalIoCount = 0;
    try {
      for (let i = 0; i < sources.length; i++) {
        const isOutput = i === 1;
        args.dataSource = sources[i];
        args.ioList = ios[i].map((curItem) => ({
          ...paraAddr2DbAddr(curItem, isOutput),
          siteId:
            parseVirtualSiteId(curItem.id ?? 0).siteId === 0 ?
              state.cached.selectedSite
            : parseVirtualSiteId(curItem.id ?? 0).siteId,
        }));
        const result = (yield this.API?.graphql({
          query: listIoDataGql,
          variables: args,
        })) as GraphQLResult<ListIoDataQuery> | undefined;
        // convert json string to object
        if (!result?.data?.listIoData) {
          yield put(raiseAction(ActionType.ACT_GET_IOTREND_FAIL, "no input trend data received"));
          return;
        }
        const newData = this.cloudIoDataToActionData(
          result.data,
          state.para.parameter as ICloudParameter,
          isOutput,
        );
        digitalIoCount += data.reduce(
          (acc, curItem) =>
            acc +
            Number(
              isDigital(
                isOutput ?
                  state.para.parameter?.output[curItem.index]
                : state.para.parameter?.input[curItem.index],
                isOutput,
              ),
            ),
          0,
        );
        data.push(...newData);
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_IOTREND_FAIL, error);
      return;
    }

    // indicator if more data is requested (AGGREGATION-COUNT DOES NOT EXCEED IO_TREND_LIMIT)
    const queryMoreData = aggregation !== Aggregation.NONE && digitalIoCount < IO_TREND_LIMIT;
    // notify action with received data
    const payload: IActionCloudIoTrendData = {
      aggregation,
      data,
      queryMoreData,
    };
    yield put(raiseAction(ActionType.ACT_GET_CLOUDIOTREND_SUCC, payload));

    // if we transmitted aggregated data, we also need to read full data for digital IO list view
    //  - but only if the count of all data-rows would not exceed our max-limit (IO_TREND_LIMIT)...
    if (queryMoreData) {
      args.aggregation = Aggregation.NONE;
      args.aggregationType = undefined;
      data = [];
      try {
        for (let i = 0; i < sources.length; i++) {
          const isOutput = i === 1;
          args.dataSource = sources[i];
          // only query digital ios
          args.ioList = ios[i]
            .filter((curItem) => isDigital(curItem, isOutput))
            .map((curItem) => paraAddr2DbAddr(curItem, isOutput));
          const result = (yield this.API?.graphql({
            query: listIoDataGql,
            variables: args,
          })) as GraphQLResult<ListIoDataQuery> | undefined;
          if (!result?.data?.listIoData) {
            yield put(raiseAction(ActionType.ACT_GET_CLOUDIOTREND_FULL_FAIL, "no IO data"));
            return;
          }
          const newData = this.cloudIoDataToActionData(
            result.data,
            state.para.parameter as ICloudParameter,
            isOutput,
          );
          data.push(...newData);
        }
        const fullPayload: IActionCloudIoTrendData = {
          aggregation: args.aggregation,
          data,
          queryMoreData: false,
        };
        yield put(raiseAction(ActionType.ACT_GET_CLOUDIOTREND_FULL_SUCC, fullPayload));
      } catch (error) {
        yield this.raiseHttpError(ActionType.ACT_GET_CLOUDIOTREND_FULL_FAIL, error);
        return;
      }
    } else {
      if (aggregation !== Aggregation.NONE)
        console.log("skip full IO request due to big amount of data", data.length, digitalIoCount);
    }
  }

  protected getUsedSites(
    selectedSite: number | undefined,
    virtualSite: IVirtSiteDefinition | undefined,
  ) {
    const siteIds: number[] = [];
    // add main site
    if (isSet(selectedSite)) {
      siteIds.push(selectedSite);
    }
    // create list of sites we are using
    Object.values(virtualSite ?? {}).forEach((curGroup) => {
      (curGroup as IVirtAssign[]).forEach((curItem) => {
        if (!siteIds.includes(curItem.sourceSite)) {
          siteIds.push(curItem.sourceSite);
        }
      });
    });
    return siteIds;
  }

  private *visibilityChange(action: IAction<boolean>) {
    const state = (yield select(this.getState)) as IState;
    const isVisible = action.payload;
    if (isVisible) {
      // typically, all subscriptions are already in place and not touched
      // however, check for timeout of last live data registration
      const lastRequest = state.scheduler.allData.lastRequest ?? 0;
      if (Date.now() - lastRequest > LIVE_DATA_REGISTRATION_TIMEOUT) {
        yield put(raiseAction(ActionType.ACT_REGISTER_FOR_PUSH_DATA_TIMEOUT));
      }
      // renew live data registration is needed and performed
      yield this.registerForPushData(true);
    } else {
      // stop live data transmission
      yield this.liveDataHandler?.unregisterPushData();
    }
  }

  protected *registerForPushData(forceUpdate: boolean) {
    const state = (yield select(this.getState)) as IState;
    // determine needed subscriptions
    const hasChargers = state.ocpp.charger.length > 0;
    const isOcppRestricted = isOcppFrontend(state.router.rootPath);
    const isRestricted = isOcppRestricted || state.router.rootPath === RootPath.TREND;
    const isRfidUser = !!state.site.user?.isRfidUser;
    const secUserId = state.site.user?.secId;
    const curUser = state.site.userList?.find(
      (curItem) => curItem.secId === state.site.user?.secId,
    );

    // we might not need subscriptions at all, e.g. when triggered via visibilityChange
    // but we did not select a site yet
    if ((isRfidUser && !isSet(secUserId)) || (!isRfidUser && !isSet(state.cached.selectedSite))) {
      console.log("skipping live data registration", isRfidUser, state.cached.selectedSite);
      return;
    }

    // when not restricted: we do not know whether we need live data yet
    // as we probably did not receive the parameters yet
    // so we assume to need them and avoid: && isSet(state.parameter)
    const selection: IRegisterPushSelection = {
      needsLiveData: !isRestricted,
      needsReportData: !isRfidUser,
      needsAlarmData: !isRestricted && !isRfidUser,
      needsChargerData: isOcppRestricted || hasChargers,
      needsControllerChargerData:
        !isRestricted && !isRfidUser && Boolean(curUser?.accessRights.showEnlineChargers),
    };
    yield this.liveDataHandler?.setupForPushData(
      state.cached.clientToken,
      isRfidUser ? undefined : state.cached.selectedSite,
      curUser?.id,
      isRfidUser ? secUserId : undefined,
      state.ocpp.pendingChargingSession,
      selection,
      forceUpdate,
      state.scheduler.liveViaProxy,
    );
  }

  // connect to the proxy when the controller is reachable through it, else fall
  // back to the GraphQL live-data push; reflected in scheduler.liveViaProxy
  protected *updateProxyConnection(proxyAvailable: boolean) {
    const state = (yield select(this.getState)) as IState;
    const isRfidUser = !!state.site.user?.isRfidUser;
    const serial = state.site.siteList?.find(
      (curSite) => curSite.id === state.cached.selectedSite,
    )?.serial;
    const canUseProxy =
      proxyAvailable && !isRfidUser && isSet(serial) && isSet(state.cached.selectedSite);

    if (canUseProxy) {
      this.proxyInterface.setConnection(serial!, state.cached.clientToken);
      try {
        yield this.proxyInterface.connect();
        if (!state.scheduler.liveViaProxy) {
          yield put(raiseAction(ActionType.ACT_SET_LIVE_VIA_PROXY, true));
          // pull the first live data set immediately
          yield put(raiseAction(ActionType.ACT_GET_ALLDATA_REQ));
        }
        return;
      } catch (error) {
        console.warn("cannot connect to live-data proxy:", error);
        this.proxyInterface.close();
      }
    } else {
      this.proxyInterface.close();
    }
    // not using the proxy (anymore): revert to the GraphQL push
    if (state.scheduler.liveViaProxy) {
      yield put(raiseAction(ActionType.ACT_SET_LIVE_VIA_PROXY, false));
    }
  }

  protected *getAllData(action: IAction<undefined>) {
    // controller live data is polled directly only while the proxy is active
    const state = (yield select(this.getState)) as IState;
    if (!state.scheduler.liveViaProxy) {
      return;
    }
    yield* daSagas.getAllData(this, this.proxyInterface);
  }

  protected *getConditionBlockStatus(action: IAction<number>) {
    // on-demand controller reads are only possible directly, i.e. while the proxy is active
    const state = (yield select(this.getState)) as IState;
    if (!state.scheduler.liveViaProxy) {
      return;
    }
    yield* daSagas.getConditionBlockStatus(this, this.proxyInterface, action);
  }

  protected *unsubscribeData(action?: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    // synchronous, so it is done before a directly following site selection subscribes again
    yield this.liveDataHandler?.unsubscribePushData();
    const router: ReturnType<typeof createBrowserRouter> = yield getContext("router");
    const target = isSet(state.router.rootPath) ? `/${state.router.rootPath}` : "/";
    router.navigate(target);
    // async, last
    yield this.liveDataHandler?.unregisterPushData();
  }

  protected *setInput(action: IAction<IActionChangeInput>) {
    const state = (yield select(this.getState)) as IState;
    // run commands directly via the proxy when it is active
    if (state.scheduler.liveViaProxy) {
      yield* daSagas.setInput(this, this.proxyInterface, action);
      return;
    }
    const module = state.para.parameter?.input[action.payload.index]?.module;
    if (module === undefined) {
      yield put(
        raiseAction(ActionType.ACT_CHANGE_INPUT_FAIL, `Illegal module ${action.payload.index}.`),
      );
      return;
    }
    const args: ExecCommandInput = {
      siteId: state.cached.selectedSite!,
      seqNr: action.payload.commandSeqNr,
      command: Command.setInput,
      ioArg: {
        address: action.payload.index,
        value: action.payload.value,
      },
    };
    try {
      const result = (yield this.API?.graphql({
        query: execCommandGql,
        variables: { data: args },
      })) as GraphQLResult<ExecCommandMutation> | undefined;
      if (result?.data.execCommand) {
        // success feedback for cloud is provided after confirmation via liveData message
      } else {
        yield put(raiseAction(ActionType.ACT_CHANGE_INPUT_FAIL, `Changing input failed.`));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_CHANGE_INPUT_FAIL, error);
    }
  }

  protected *setOutput(action: IAction<IActionChangeOutput>) {
    const state = (yield select(this.getState)) as IState;
    // run commands directly via the proxy when it is active
    if (state.scheduler.liveViaProxy) {
      yield* daSagas.setOutput(this, this.proxyInterface, action);
      return;
    }
    const args: ExecCommandInput = {
      siteId: state.cached.selectedSite!,
      seqNr: action.payload.commandSeqNr,
      command: Command.setOutput,
      ioArg: {
        address: action.payload.index,
        value: action.payload.cmd,
      },
    };
    try {
      const result = (yield this.API?.graphql({
        query: execCommandGql,
        variables: { data: args },
      })) as GraphQLResult<ExecCommandMutation> | undefined;
      if (result?.data.execCommand) {
        // success feedback for cloud is provided after confirmation via liveData message
      } else {
        yield put(raiseAction(ActionType.ACT_CHANGE_OUTPUT_FAIL, `Changing output failed.`));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_CHANGE_OUTPUT_FAIL, error);
    }
  }

  protected *sendAlarmAck(ack: IActionAckAlarm) {
    const state = (yield select(this.getState)) as IState;
    const args: NewAlarmDataInput = {
      siteId: state.cached.selectedSite!,
      alarmTimestamp: ack.alarmTimestamp,
      source: ack.source,
      value: ack.value,
      isActive: false,
      ackTimestamp: Math.round(Date.now() / 1000),
      ackSource: state.site.user?.secId ?? "enline cloud",
    };
    const apiResult = (yield this.API?.graphql({
      query: newAlarmDataGql,
      variables: { data: args },
    })) as GraphQLResult<NewAlarmDataMutation> | undefined;
    return apiResult ? true : false;
  }

  protected *changeTrendData(action: IAction<IActionChangeTrend>) {
    const state = (yield select(this.getState)) as IState;
    const tlId =
      state.para.parameter?.tariffline[action.payload.tlIndex]?.id ?? action.payload.tlIndex;
    try {
      const args: NewTrendDataInput = {
        siteId: state.cached.selectedSite!,
        tlId,
        timestamp: action.payload.timestamp,
        regperiod: action.payload.regperiod,
        energy: action.payload.energy,
        costs: action.payload.costs,
        tariff: action.payload.tariff,
        targetpower: action.payload.targetpower,
      };
      const result = (yield this.API?.graphql({
        query: newTrendDataGql,
        variables: { data: args },
      })) as GraphQLResult<NewTrendDataMutation> | undefined;
      if (result) {
        const trendRow = cloudTrend2LinxTrend({
          ...action.payload,
          power: (action.payload.energy * 3600) / action.payload.regperiod,
          tlId,
          siteId: state.cached.selectedSite!,
        });
        yield put(raiseAction(ActionType.ACT_CHANGE_TREND_SUCC, trendRow));
      } else {
        yield put(raiseAction(ActionType.ACT_CHANGE_TREND_FAIL));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_CHANGE_TREND_FAIL, error);
    }
  }

  private mapChargerRow(
    curRow: NonNullable<ListChargersQuery["listChargers"]>[number],
  ): IActionOcppCharger {
    return {
      fk_charger: parseApiInt(curRow?.id),
      chargepoint: parseApiString(curRow?.chargepoint).split("@")[0], // strip site name
      isActive: parseApiBool(curRow?.isActive),
      connectionType: (curRow?.connectionType as ChargerConnectionType | null) ?? undefined,
      // ocpp configuration data
      vendor: curRow?.vendor ?? undefined, // null -> undefined
      model: curRow?.model ?? undefined, // null -> undefined
      firmware: curRow?.firmware ?? undefined, // null -> undefined
      serial: curRow?.serial ?? undefined, // null -> undefined
      numberofconnectors:
        isSet(curRow?.numberofconnectors) ? parseApiInt(curRow?.numberofconnectors) : undefined,
      hasSmartCharging: parseApiBool(curRow?.hasSmartCharging),
      // user configuration data
      name: curRow?.name ?? undefined, // null -> undefined
      nominalPower: curRow?.nominalPower ?? undefined, // null -> undefined
      restrictAuth: parseApiBool(curRow?.restrictAuth),
      coordinates:
        curRow?.coordinates ? parseApiItemArray(curRow.coordinates, parseApiFloat) : undefined,
      tariffs: parseApiItemArray(curRow?.tariffs),
      // extended data
      lastMessage: curRow?.lastMessage ?? undefined, // null -> undefined
    };
  }

  protected *getOcppChargers(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: ListChargersQueryVariables = {
      siteId: state.cached.selectedSite,
      rfid: state.site.user?.isRfidUser ? state.site.user.secId : undefined,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listChargers,
        variables: args,
      })) as GraphQLResult<ListChargersQuery> | undefined;
      const chargerList = (result?.data?.listChargers ?? []).map((curRow) =>
        this.mapChargerRow(curRow),
      );
      yield put(raiseAction(ActionType.ACT_GET_OCPP_CHARGERS_SUCC, chargerList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_OCPP_CHARGERS_FAIL, error);
    }
  }

  // load chargers of a site other than the currently selected one, e.g. to populate the
  // charger-restriction selection of an rfid card that is valid for several sites
  protected *getRfidSiteChargers(siteId: number) {
    const args: ListChargersQueryVariables = { siteId };
    try {
      const result = (yield this.API?.graphql({
        query: listChargers,
        variables: args,
      })) as GraphQLResult<ListChargersQuery> | undefined;
      const chargerList = (result?.data?.listChargers ?? []).map((curRow) =>
        this.mapChargerRow(curRow),
      );
      const payload: IActionRfidSiteChargers = { siteId, chargers: chargerList };
      yield put(raiseAction(ActionType.ACT_GET_RFID_SITE_CHARGERS_SUCC, payload));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_RFID_SITE_CHARGERS_FAIL, error);
    }
  }

  protected *saveOcppCharger(action: IAction<IActionChangeChargerSettings>) {
    const chargerReq: ModifyChargerInput = {
      id: `${action.payload.id}`,
      deleteCharger: action.payload.deleteCharger,
      name: action.payload.name,
      nominalPower: action.payload.nominalPower,
      restrictAuth: action.payload.restrictAuth,
      coordinates: action.payload.coordinates,
    };
    try {
      // first, modify charger
      const result = (yield this.API?.graphql({
        query: modifyChargerGql,
        variables: { data: chargerReq },
      })) as GraphQLResult<ModifyChargerMutation> | undefined;
      if (!result?.data.modifyCharger) {
        yield put(raiseAction(ActionType.ACT_SAVE_OCPP_CHARGERS_FAIL));
      }
      // second, modify all connectors
      if (action.payload.connector) {
        for (let curId = 0; curId < action.payload.connector.length; curId++) {
          if (action.payload.connector[curId]) {
            const connectorData = action.payload.connector[curId];
            const connectorReq: ModifyConnectorInput = {
              ...connectorData,
              chargerId: action.payload.id,
              connectorId: curId,
            };
            const result = (yield this.API?.graphql({
              query: modifyConnectorGql,
              variables: { data: connectorReq },
            })) as GraphQLResult<ModifyConnectorMutation> | undefined;
            if (!result?.data.modifyConnector) {
              yield put(raiseAction(ActionType.ACT_SAVE_OCPP_CHARGERS_FAIL));
            }
          }
        }
      }
      yield put(raiseAction(ActionType.ACT_SAVE_OCPP_CHARGERS_SUCC, action.payload));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_SAVE_OCPP_CHARGERS_FAIL, error);
    }
  }

  protected *getOcppRfidsBasic(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: ListRfidsQueryVariables = {
      siteId: state.cached.selectedSite,
      rfid: state.site.user?.isRfidUser ? state.site.user.secId : undefined,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listRfidsBasic,
        variables: args,
      })) as GraphQLResult<ListRfidsBasicQuery> | undefined;
      const rfidList = (result?.data?.listRfids ?? []).map((curRow) => {
        const rfid: IRfid = {
          id: parseApiInt(curRow?.id),
          siteId: parseApiInt(curRow?.siteId),
          rfid: parseApiString(curRow?.rfid),
          category: parseApiString(curRow?.category),
          isActive: parseApiBool(curRow?.isActive),
          name: curRow?.name ?? undefined,
          chargerRestricted: parseApiBool(curRow?.chargerRestricted),
          limitStart: curRow?.limitStart ?? undefined, // null -> undefined
          limitEnd: curRow?.limitEnd ?? undefined, // null -> undefined
          limitEnergy: curRow?.limitEnergy ?? undefined, // null -> undefined
          limitCost: curRow?.limitCost ?? undefined, // null -> undefined
          limitPeriod: (curRow?.limitPeriod as eRfidLimitPeriod | null) ?? undefined, // null -> undefined
          maxPowerAc: curRow?.maxPowerAc ?? undefined, // null -> undefined
          maxPowerDc: curRow?.maxPowerDc ?? undefined, // null -> undefined
          accessRights: JSON.parse(curRow?.accessRights ? curRow?.accessRights : "{}") ?? undefined, // null -> undefined
          carName: curRow?.carName ?? undefined,
          carBattery: curRow?.carBattery ?? undefined,
          additionalData: curRow?.additionalData ?? undefined, // null -> undefined
        };
        return rfid;
      }) as IActionTypeRfidList;
      yield put(raiseAction(ActionType.ACT_GET_OCPP_RFIDS_BASIC_SUCC, rfidList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_OCPP_RFIDS_BASIC_FAIL, error);
    }
  }

  protected *getOcppRfidsExt(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: ListRfidsQueryVariables = {
      siteId: state.cached.selectedSite,
      rfid: state.site.user?.isRfidUser ? state.site.user.secId : undefined,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listRfids,
        variables: args,
      })) as GraphQLResult<ListRfidsQuery> | undefined;
      const rfidList = (result?.data?.listRfids ?? []).map((curRow) => {
        const rfid: IRfid = {
          id: parseApiInt(curRow?.id),
          siteId: parseApiInt(curRow?.siteId),
          rfid: parseApiString(curRow?.rfid),
          siteIds: parseApiItemArray(curRow?.siteIds, parseApiInt),
          category: parseApiString(curRow?.category),
          isActive: parseApiBool(curRow?.isActive),
          name: curRow?.name ?? undefined,
          chargerRestricted: parseApiBool(curRow?.chargerRestricted),
          chargers: parseApiItemArray(curRow?.chargers, parseApiInt),
          tariffs: parseApiItemArray(curRow?.tariffs),
          limitStart: curRow?.limitStart ?? undefined, // null -> undefined
          limitEnd: curRow?.limitEnd ?? undefined, // null -> undefined
          limitEnergy: curRow?.limitEnergy ?? undefined, // null -> undefined
          limitCost: curRow?.limitCost ?? undefined, // null -> undefined
          limitPeriod: (curRow?.limitPeriod as eRfidLimitPeriod | null) ?? undefined, // null -> undefined
          maxPowerAc: curRow?.maxPowerAc ?? undefined, // null -> undefined
          maxPowerDc: curRow?.maxPowerDc ?? undefined, // null -> undefined
          accessRights: JSON.parse(curRow?.accessRights ? curRow?.accessRights : "{}") ?? undefined, // null -> undefined
          carName: curRow?.carName ?? undefined,
          carBattery: curRow?.carBattery ?? undefined,
          usedEnergy: curRow?.usedEnergy ?? undefined, // null -> undefined
          usedCost: curRow?.usedCost ?? undefined, // null -> undefined
          lastUsage: curRow?.lastUsage ?? undefined, // null -> undefined
          additionalData: curRow?.additionalData ?? undefined, // null -> undefined
        };
        return rfid;
      }) as IActionTypeRfidList;
      yield put(raiseAction(ActionType.ACT_GET_OCPP_RFIDS_EXT_SUCC, rfidList));

      // rfids may be valid for sites other than the current one: preload their chargers so the
      // charger-restriction selection has them available once a card is opened for editing
      const linkedSiteIds = new Set<number>();
      rfidList.forEach((curRfid) =>
        curRfid.siteIds?.forEach((curSiteId) => linkedSiteIds.add(curSiteId)),
      );
      const uncachedSiteIds = Array.from(linkedSiteIds).filter(
        (curSiteId) =>
          curSiteId !== state.cached.selectedSite &&
          !state.ocpp.rfidSiteChargers?.some((curEntry) => curEntry.siteId === curSiteId),
      );
      // load in small batches: cards may be linked to 50+ sites, and firing all requests at once
      // exceeds the lambda concurrency limit of the charger API
      for (let i = 0; i < uncachedSiteIds.length; i += RFID_SITE_CHARGERS_BATCH) {
        yield all(
          uncachedSiteIds
            .slice(i, i + RFID_SITE_CHARGERS_BATCH)
            .map((curSiteId) => call([this, this.getRfidSiteChargers], curSiteId)),
        );
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_OCPP_RFIDS_EXT_FAIL, error);
    }
  }

  protected *checkOcppRfid(action: IAction<string>) {
    const args: CheckRfidQueryVariables = { rfid: action.payload };
    try {
      const result = (yield this.API?.graphql({
        query: checkRfid,
        variables: args,
      })) as GraphQLResult<CheckRfidQuery> | undefined;
      const check = result?.data?.checkRfid ?? undefined;
      yield put(raiseAction(ActionType.ACT_CHECK_OCPP_RFIDS_SUCC, check));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_CHECK_OCPP_RFIDS_FAIL, error);
    }
  }

  protected *saveOcppRfid(action: IAction<IActionChangeRfid>) {
    const state = (yield select(this.getState)) as IState;
    const { id, isLoginAllowed, isChargerPowerCtrlAllowed, ...data } = action.payload;
    const args: ModifyRfidInput = {
      id: isSet(id) ? `${id}` : undefined,
      siteId: state.cached.selectedSite!,
      ...data,
      accessRights: JSON.stringify({ isLoginAllowed, isChargerPowerCtrlAllowed }),
    };
    try {
      const result = (yield this.API?.graphql({
        query: modifyRfidGql,
        variables: { data: args },
      })) as GraphQLResult<ModifyRfidMutation> | undefined;
      if (result?.data.modifyRfid) {
        yield put(raiseAction(ActionType.ACT_SAVE_OCPP_RFID_SUCC, action.payload));
        // reload rfid data
        yield put(raiseAction(ActionType.ACT_GET_OCPP_RFIDS_EXT_REQ));
      } else {
        yield put(raiseAction(ActionType.ACT_SAVE_OCPP_RFID_FAIL));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_SAVE_OCPP_RFID_FAIL, error);
    }
  }

  protected *getOcppRefusedRfids(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: ListRefusedRfidsQueryVariables = {
      siteId: state.cached.selectedSite!,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listRefusedRfids,
        variables: args,
      })) as GraphQLResult<ListRefusedRfidsQuery> | undefined;
      const rfidList = (result?.data?.listRefusedRfids ?? []).map((curRow) => {
        const rfid: IRefusedRfid = {
          charger: parseApiInt(curRow?.charger),
          rfid: parseApiString(curRow?.rfid),
          firstAttempt: parseApiInt(curRow?.firstAttempt),
          lastAttempt: parseApiInt(curRow?.lastAttempt),
          counter: parseApiInt(curRow?.counter),
        };
        return rfid;
      });
      yield put(raiseAction(ActionType.ACT_GET_OCPP_REFUSED_RFIDS_SUCC, rfidList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_OCPP_REFUSED_RFIDS_FAIL, error);
    }
  }

  protected *getOcppTariffs(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: ListTariffsQueryVariables = {
      siteId: state.site.user?.isRfidUser ? undefined : state.cached.selectedSite,
      rfid: state.site.user?.isRfidUser ? state.site.user.secId : undefined,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listTariffs,
        variables: args,
      })) as GraphQLResult<ListTariffsQuery> | undefined;
      const tariffList = (result?.data?.listTariffs ?? []).map((curRow) => {
        const tariff: ITariff = {
          id: parseApiInt(curRow?.id),
          siteId: isSet(curRow?.siteId) ? parseApiInt(curRow?.siteId) : undefined,
          name: parseApiString(curRow?.name),
          currency: parseApiString(curRow?.currency),
          isActive: isSet(curRow?.isActive) ? parseApiBool(curRow!.isActive) : undefined,
          priority: isSet(curRow?.priority) ? parseApiInt(curRow!.priority) : undefined,
          costPerEnergy: curRow?.costPerEnergy ?? undefined, // null -> undefined
          costPerPeriod: curRow?.costPerPeriod ?? undefined, // null -> undefined
          validFrom: curRow?.validFrom ?? undefined, // null -> undefined
          validUntil: curRow?.validUntil ?? undefined, // null -> undefined
          timeStart: curRow?.timeStart ?? undefined, // null -> undefined
          timeEnd: curRow?.timeEnd ?? undefined, // null -> undefined
        };
        return tariff;
      });
      yield put(raiseAction(ActionType.ACT_GET_OCPP_SITE_TARIFFS_SUCC, tariffList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_OCPP_SITE_TARIFFS_FAIL, error);
    }
  }

  protected *saveOcppTariff(action: IAction<IActionChangeTariff>) {
    const state = (yield select(this.getState)) as IState;
    const { id, ...data } = action.payload;
    const args: ModifyTariffInput = {
      id: isSet(id) ? `${id}` : undefined,
      siteId: state.cached.selectedSite!,
      ...data,
    };
    try {
      const result = (yield this.API?.graphql({
        query: modifyTariffGql,
        variables: { data: args },
      })) as GraphQLResult<ModifyTariffMutation> | undefined;
      if (result?.data.modifyTariff) {
        yield put(raiseAction(ActionType.ACT_SAVE_OCPP_TARIFF_SUCC, action.payload));
        // reload tariff data, depending on modification
        if (isSet(args.rfidId)) {
          yield put(raiseAction(ActionType.ACT_GET_OCPP_RFIDS_EXT_REQ));
        } else if (isSet(args.chargerId)) {
          yield put(raiseAction(ActionType.ACT_GET_OCPP_CHARGERS_REQ));
        } else {
          yield put(raiseAction(ActionType.ACT_GET_OCPP_SITE_TARIFFS_REQ));
        }
      } else {
        yield put(raiseAction(ActionType.ACT_SAVE_OCPP_TARIFF_FAIL));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_SAVE_OCPP_TARIFF_FAIL, error);
    }
  }

  protected *getOcppConnectors(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: ListConnectorsQueryVariables = {
      siteId: state.cached.selectedSite,
      rfid: state.site.user?.isRfidUser ? state.site.user.secId : undefined,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listConnectors,
        variables: args,
      })) as GraphQLResult<ListConnectorsQuery> | undefined;
      const connectorList = (result?.data?.listConnectors ?? []).map((curRow) => {
        const connector: IActionOcppConnector = {
          fk_charger: parseApiInt(curRow?.chargerId),
          connector: parseApiInt(curRow?.connectorId),
          // user configuration data
          lastChange: parseApiFloat(curRow?.lastUpdate),
          name: curRow?.name ?? undefined, // null -> undefined
          evseid: curRow?.evseid ?? undefined, // null -> undefined
          plugType: curRow?.plugType ?? undefined, // null -> undefined
          nominalPower: curRow?.nominalPower ?? undefined, // null -> undefined
          isDc: curRow?.isDc ?? undefined, // null -> undefined
          nrPhases: curRow?.nrPhases ?? undefined, // null -> undefined
          // ocpp data
          lastUpdate: parseApiFloat(curRow?.lastUpdate),
          power: curRow?.power ?? undefined,
          current: parseApiItemArray(curRow?.current, (item) => item ?? 0),
          voltage:
            curRow?.voltage && curRow.voltage.length ?
              parseApiItemArray(curRow.voltage, (item) => item ?? 0)
            : [],
          sessionEnergy: curRow?.sessionenergy ?? undefined, // null -> undefined
          energymeter: curRow?.energymeter ?? undefined, // null -> undefined
          connectorStatus: (curRow?.connectorStatus as ConnectorStatus | null) ?? undefined,
          connectorStatusTimestamp: parseApiFloat(curRow?.connectorStatusTimestamp),
        };
        return connector;
      });
      yield put(raiseAction(ActionType.ACT_GET_OCPP_CONNECTORS_SUCC, connectorList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_OCPP_CONNECTORS_FAIL, error);
    }
  }

  protected *getOcppSessions(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    // when filtering ocpp sessions
    // - charger/connector/rfid is taken from state.router
    // - the rest is taken from state.gui.chargingSessionFilter
    const charger = state.ocpp.charger.find(
      (curItem) => curItem.fk_charger === state.router.charger,
    );
    const rfid = state.ocpp.rfid.find((curItem) => curItem.id === state.router.rfid);
    // if we selected a charger, it must be an OCPP charger
    if (isSet(charger) && charger.fk_charger === undefined) {
      // non-OCPP charger; return empty session list
      yield put(raiseAction(ActionType.ACT_GET_OCPP_SESSIONS_SUCC, []));
      return;
    }
    // no charger, or OCPP charger; setup query filters
    const { graphStart, graphEnd } = getGraphBoundaryDatesFromPeriod(
      state.gui.chargingSessionFilter,
      state.site.tzOffset,
      state.gui.chargingSessionFilter.timePeriod ?? TimePeriod.MONTH,
    );
    const args: ListChargingSessionsQueryVariables = {
      siteId: state.cached.selectedSite,
      chargerId: charger ? charger.fk_charger : undefined,
      connector: state.router.connector,
      startDate: graphStart,
      endDate: graphEnd,
      rfid: state.site.user?.isRfidUser ? state.site.user.secId : rfid?.rfid,
      user: state.gui.chargingSessionFilter.user,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listChargingSessions,
        variables: args,
      })) as GraphQLResult<ListChargingSessionsQuery> | undefined;
      const chargerList = (result?.data?.listChargingSessions ?? []).map((curRow) => {
        const chargerSession: IOcppChargingSession = {
          id: parseApiInt(curRow?.id),
          fk_charger: parseApiInt(curRow?.chargerId),
          fk_tariff: curRow?.tariffId ?? undefined,
          startTimestamp: parseApiInt(curRow?.startDate),
          stopTimestamp: curRow?.stopDate ? parseApiInt(curRow?.stopDate) : undefined,
          connector: parseApiInt(curRow?.connector),
          rfid: curRow?.rfid ? parseApiString(curRow?.rfid) : undefined,
          rfidId: curRow?.rfidId ? parseApiInt(curRow?.rfidId) : undefined,
          startMeter: parseApiInt(curRow?.startMeter),
          stopMeter: curRow?.stopDate ? parseApiInt(curRow?.stopMeter) : undefined,
          stopReason: curRow?.stopReason ? (curRow.stopReason as StopReason) : undefined,
          costs: isSet(curRow?.costs) ? parseApiFloat(curRow!.costs) : undefined,
        };
        return chargerSession;
      }) as IActionTypeChargingSessionList;
      yield put(raiseAction(ActionType.ACT_GET_OCPP_SESSIONS_SUCC, chargerList));
      // as we got new session data, we might face a new pending session
      // in this case we must renew out livedata subscriptions as an RFID user only
      // subscribes to pending sessions
      if (!state.scheduler.isInBackground) {
        yield put(raiseAction(ActionType.ACT_REGISTER_FOR_PUSH_DATA_REQ, false));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_OCPP_SESSIONS_FAIL, error);
    }
  }

  protected *getPendingOcppSessions(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: ListChargingSessionsQueryVariables = {
      siteId: state.cached.selectedSite,
      rfid: state.site.user?.isRfidUser ? state.site.user.secId : undefined,
      endDate: 0,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listChargingSessions,
        variables: args,
      })) as GraphQLResult<ListChargingSessionsQuery> | undefined;
      const sessionList = (result?.data?.listChargingSessions ?? []).map((curRow) => {
        const session: IOcppChargingSession = {
          id: parseApiInt(curRow?.id),
          fk_charger: parseApiInt(curRow?.chargerId),
          fk_tariff: curRow?.tariffId ?? undefined,
          startTimestamp: parseApiInt(curRow?.startDate),
          connector: parseApiInt(curRow?.connector),
          rfid: curRow?.rfid ? parseApiString(curRow?.rfid) : undefined,
          rfidId: curRow?.rfidId ? parseApiInt(curRow?.rfidId) : undefined,
          startMeter: parseApiInt(curRow?.startMeter),
        };
        return session;
      }) as IActionTypeChargingSessionList;
      // potentially also subscribe to session connector data
      if (state.site.user?.isRfidUser) {
        for (let i = 0; i < sessionList.length; i++) {
          yield this.liveDataHandler?.subscribeChargerDataForConnector(
            sessionList[i].fk_charger,
            sessionList[i].connector,
            true,
          );
        }
      }
      yield put(raiseAction(ActionType.ACT_GET_PENDING_OCPP_SESSIONS_SUCC, sessionList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_PENDING_OCPP_SESSIONS_FAIL, error);
    }
  }

  protected *getChargingSession(action: IAction<number>) {
    const state = (yield select(this.getState)) as IState;
    const transactionId = action.payload;
    let session = state.ocpp.chargingSession.find((curItem) => curItem.id === transactionId);
    // if session is not found, also look in pending session list
    if (session === undefined) {
      session = state.ocpp.pendingChargingSession.find((curItem) => curItem.id === transactionId);
    }
    if (!session) {
      console.error("no charging session to get");
      return;
    }
    const siteId = state.site.user?.isRfidUser ? undefined : state.cached.selectedSite;
    const rfid = state.site.user?.isRfidUser ? state.site.user.secId : undefined;
    // run all these query in parallel
    yield all({
      states: call(this.getChargingSessionStates.bind(this, session, Boolean(state.site.user))),
      trending: call(this.getChargingSessionTrending.bind(this, session, siteId, rfid)),
      control: call(this.getChargingSessionControl.bind(this, session.fk_charger, transactionId)),
      tariffs: call(this.getChargingSessionTariffs.bind(this, session, siteId, rfid)),
    });
  }

  private *getChargingSessionStates(session: IOcppChargingSession, isRfidUser: boolean) {
    const stateArgs: ListChargerStatusNotificationsQueryVariables = {
      chargerId: session?.fk_charger ?? 0,
      // rfid users only get data of their own connector
      connector: isRfidUser ? session?.connector : undefined,
      // we query all connectors to include charger station state events
      // we extend the time period slightly to catch the start and stop event
      startDate: session.startTimestamp ? session.startTimestamp - 10 : undefined,
      endDate: session.stopTimestamp ? session.stopTimestamp + 10 : undefined,
    };
    // the state events
    try {
      const result = (yield this.API?.graphql({
        query: listChargerStatusNotifications,
        variables: stateArgs,
      })) as GraphQLResult<ListChargerStatusNotificationsQuery> | undefined;
      // drop the bordering rows before/after the period, which the backend adds for timelines
      const { startDate, endDate } = stateArgs;
      const rows = (result?.data?.listChargerStatusNotifications ?? []).filter(
        (curRow) =>
          (!isSet(startDate) || curRow.timestamp >= startDate) &&
          (!isSet(endDate) || curRow.timestamp < endDate),
      );
      const stateList = rows.map((curRow) => {
        const stateEvent: IChargerStateEvent = {
          id: parseApiInt(curRow?.id),
          fk_charger: parseApiInt(curRow?.chargerId),
          connector: parseApiInt(curRow?.connector),
          timestamp: parseApiInt(curRow?.timestamp),
          source: curRow.source,
          statusCode: (curRow?.statusCode as ConnectorStatus) ?? ConnectorStatus.UNAVAILABLE,
          errorCode: parseApiString(curRow?.errorCode),
          info: curRow?.info ? parseApiString(curRow?.info) : undefined,
        };
        return stateEvent;
      }) as IActionTypeChargerStateChangeList;
      yield put(raiseAction(ActionType.ACT_GET_CHARGING_STATES_SUCC, stateList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_CHARGING_STATES_FAIL, error);
    }
  }

  private *getChargingSessionTrending(
    session: IOcppChargingSession,
    siteId: number | undefined,
    rfid: string | undefined,
  ) {
    // get trending data
    const trendArgs: ListTrendDataQueryVariables = {
      siteId: siteId ?? 0, // not used for RFID users
      rfid,
      chargerId: session?.fk_charger,
      startDate: session?.startTimestamp,
      endDate: session?.stopTimestamp,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listTrendDataGql,
        variables: trendArgs,
      })) as GraphQLResult<ListTrendDataQuery> | undefined;
      // convert json string to object
      const data: IActionCloudTrendDataRow[] =
        result?.data?.listTrendData?.map((curData) => {
          return cloudTrend2LinxTrend(curData!);
        }) ?? [];
      yield put(raiseAction(ActionType.ACT_GET_CHARGERTREND_SUCC, data));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_TREND_FAIL, error);
    }
  }

  private *getChargingSessionControl(chargerId: number, transactionId: number) {
    // charging control data
    const controlArgs: GetChargerControlQueryVariables = {
      chargerId,
      transactionId,
    };
    try {
      const result = (yield this.API?.graphql({
        query: getChargerControl,
        variables: controlArgs,
      })) as GraphQLResult<GetChargerControlQuery> | undefined;
      yield put(
        raiseAction(ActionType.ACT_GET_CHARGER_POWER_SUCC, result?.data?.getChargerControl),
      );
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_CHARGER_POWER_FAIL, error);
    }
  }

  private *getChargingSessionTariffs(
    session: IOcppChargingSession,
    siteId: number | undefined,
    rfid: string | undefined,
  ) {
    // cost tariffs
    const tariffArgs: ListTariffsQueryVariables = {
      siteId,
      rfid,
      startDate: session?.startTimestamp ? session.startTimestamp - 10 : undefined,
      endDate: session?.stopTimestamp ? session?.stopTimestamp + 10 : undefined,
    };
    try {
      const result = (yield this.API?.graphql({
        query: listTariffs,
        variables: tariffArgs,
      })) as GraphQLResult<ListTariffsQuery> | undefined;
      const tariffList = (result?.data?.listTariffs ?? []).map((curRow) => {
        const tariff: ITariff = {
          id: parseApiInt(curRow?.id),
          siteId: isSet(curRow?.siteId) ? parseApiInt(curRow?.siteId) : undefined,
          name: parseApiString(curRow?.name),
          currency: parseApiString(curRow?.currency),
          isActive: parseApiBool(curRow?.isActive),
          priority: parseApiInt(curRow?.priority),
          costPerEnergy: curRow?.costPerEnergy ?? undefined, // null -> undefined
          costPerPeriod: curRow?.costPerPeriod ?? undefined, // null -> undefined
          validFrom: curRow?.validFrom ?? undefined, // null -> undefined
          validUntil: curRow?.validUntil ?? undefined, // null -> undefined
          timeStart: curRow?.timeStart ?? undefined, // null -> undefined
          timeEnd: curRow?.timeEnd ?? undefined, // null -> undefined
        };
        return tariff;
      });
      yield put(raiseAction(ActionType.ACT_GET_OCPP_SESSION_TARIFFS_SUCC, tariffList));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_OCPP_SESSION_TARIFFS_FAIL, error);
    }
  }

  protected *newChargingSession(action: IAction<IActionNewChargingSessionData>) {
    // in case of rfid users, we need to subscribe for live updates of a new session
    // or unsubscribe for closed sessions
    const state = (yield select(this.getState)) as IState;
    if (state.site.user?.isRfidUser) {
      yield this.liveDataHandler?.subscribeChargerDataForConnector(
        action.payload.chargerId,
        action.payload.connector,
        !isSet(action.payload.stopDate),
      );
    }
  }

  protected *triggerChargerCommand(action: IAction<IActionChargerCommand>) {
    const args: TriggerChargerCommandMutationVariables = {
      chargerId: action.payload.chargerId,
      command: action.payload.cmd,
      args: action.payload.cmdArgs ? JSON.stringify(action.payload.cmdArgs) : undefined,
      seqNr: makeCommandSeqNr(action.payload.timestampMs),
    };
    try {
      const result = (yield this.API?.graphql({
        query: triggerChargerCommandGql,
        variables: args,
      })) as GraphQLResult<TriggerChargerCommandMutation> | undefined;
      if (result?.data.triggerChargerCommand) {
        yield put(raiseAction(ActionType.ACT_TRIGGER_CHARGER_CMD_SUCC));
      } else {
        yield put(raiseAction(ActionType.ACT_TRIGGER_CHARGER_CMD_FAIL));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_TRIGGER_CHARGER_CMD_FAIL, error);
    }
  }

  protected *saveChargerPower(action: IAction<IActionChargingPower>) {
    const { chargerId, transactionId, mode, ...data } = action.payload;
    const args: ModifyChargerControlMutationVariables = {
      chargerId,
      transactionId,
      data: {
        ...data,
        mode: mode as string as ApiChargingMode,
      },
    };
    try {
      const chargerResult = (yield this.API?.graphql({
        query: modifyChargerControlGql,
        variables: args,
      })) as GraphQLResult<ModifyChargerControlMutation>;
      if (!chargerResult?.data.modifyChargerControl) {
        yield put(raiseAction(ActionType.ACT_SAVE_CHARGER_POWER_FAIL));
        return;
      }
      // if we modified an ongoing session (not default control),
      // also trigger ocpp command to immediate react on new settings
      let cmdResult: GraphQLResult<TriggerChargerCommandMutation> | undefined;
      if (chargerResult && transactionId) {
        const cmdArgs: TriggerChargerCommandMutationVariables = {
          chargerId,
          command: ChargerCommand.TriggerPowerControl,
          args: JSON.stringify({ transactionId }),
          seqNr: makeCommandSeqNr(),
        };
        cmdResult = (yield this.API?.graphql({
          query: triggerChargerCommandGql,
          variables: cmdArgs,
        })) as GraphQLResult<TriggerChargerCommandMutation> | undefined;
      }
      if (cmdResult?.data.triggerChargerCommand) {
        yield put(raiseAction(ActionType.ACT_SAVE_CHARGER_POWER_SUCC, action.payload));
      } else {
        yield put(raiseAction(ActionType.ACT_SAVE_CHARGER_POWER_FAIL, "failed to trigger"));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_SAVE_CHARGER_POWER_FAIL, error);
    }
  }

  protected *openSshTunnel(action: IAction<IActionCommand>) {
    const state = (yield select(this.getState)) as IState;
    const args: ExecCommandInput = {
      siteId: state.cached.selectedSite!,
      seqNr: action.payload.commandSeqNr,
      command: Command.openSshTunnel,
    };
    try {
      const result = (yield this.API?.graphql({
        query: execCommandGql,
        variables: { data: args },
      })) as GraphQLResult<ExecCommandMutation> | undefined;
      if (result?.data.execCommand) {
        // success feedback for cloud is provided after confirmation via liveData message
      } else {
        yield put(raiseAction(ActionType.ACT_OPEN_SSH_TUNNEL_FAIL, `Open tunnel failed.`));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_OPEN_SSH_TUNNEL_FAIL, error);
    }
  }

  protected *doReboot(action: IAction<IActionCommand>) {
    const state = (yield select(this.getState)) as IState;
    const args: ExecCommandInput = {
      siteId: state.cached.selectedSite!,
      seqNr: action.payload.commandSeqNr,
      command: Command.rebootController,
    };
    try {
      const result = (yield this.API?.graphql({
        query: execCommandGql,
        variables: { data: args },
      })) as GraphQLResult<ExecCommandMutation> | undefined;
      if (result?.data.execCommand) {
        // success feedback for cloud is provided after confirmation via liveData message
      } else {
        yield put(raiseAction(ActionType.ACT_REBOOT_FAIL, `Controller reboot failed.`));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_REBOOT_FAIL, error);
    }
  }

  protected *performFileUpload(action: IAction<IActionImporterFileUpload>) {
    const state = (yield select(this.getState)) as IState;
    const args: ImportFileInput = {
      siteId: state.cached.selectedSite!,
      fileType: action.payload.fileType,
      args: JSON.stringify(action.payload.args),
    };
    const file = action.payload.file;
    console.assert(Boolean(file), "No upload file");

    // first, get pre-signed url to upload the file to
    let url: string | undefined;
    try {
      const result = (yield this.API?.graphql({
        query: importFileGql,
        variables: { data: args },
      })) as GraphQLResult<ImportFileMutation> | undefined;
      if (result && !result.errors) {
        url = result.data.importFile?.url;
      }
      if (!isSet(url)) {
        yield put(
          raiseAction(ActionType.ACT_PERFORM_FILE_UPLOAD_FAIL, "No URL received for Upload!"),
        );
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_PERFORM_FILE_UPLOAD_FAIL, error);
    }

    // Use fetch to upload the file to the presigned URL
    if (isSet(url)) {
      try {
        const upload_result = (yield fetch(url, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        })) as Response;
        if (upload_result.ok) {
          yield put(raiseAction(ActionType.ACT_PERFORM_FILE_UPLOAD_SUCC));
        } else {
          yield put(raiseAction(ActionType.ACT_PERFORM_FILE_UPLOAD_FAIL, upload_result));
        }
        // initial requst of current file-uploads
        yield put(raiseAction(ActionType.ACT_GET_FILEUPLOADS_REQ));
      } catch (error) {
        yield this.raiseHttpError(ActionType.ACT_PERFORM_FILE_UPLOAD_FAIL, error);
      }
    }
  }

  protected *getFileUploads(action: IAction<undefined>) {
    const state = (yield select(this.getState)) as IState;
    const args: ListImportFilesQueryVariables = { siteId: state.cached.selectedSite! };
    try {
      const response = (yield this.API?.graphql({
        query: listImportFiles,
        variables: args,
      })) as GraphQLResult<ListImportFilesQuery> | undefined;
      yield put(raiseAction(ActionType.ACT_GET_FILEUPLOADS_SUCC, response?.data.listImportFiles));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_GET_FILEUPLOADS_FAIL, error);
    }
  }

  protected *saveVirtualParameters(action: IAction<IActionSaveVirtualParameters>) {
    const args: SetParameterInput = {
      siteId: action.payload.siteId,
      parameterVirtual: JSON.stringify(action.payload.virtualSite),
    };
    try {
      const result = (yield this.API?.graphql({
        query: setParameterGql,
        variables: { data: args },
      })) as GraphQLResult<SetParameterMutation> | undefined;
      if (result?.data.setParameter) {
        yield put(raiseAction(ActionType.ACT_SAVE_VIRTUAL_PARAMETERS_SUCC, action.payload));
        // reload parameters to reflect changes
        yield put(raiseAction(ActionType.ACT_GET_PARAMETER_REQ));
      } else {
        yield put(raiseAction(ActionType.ACT_SAVE_VIRTUAL_PARAMETERS_FAIL));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_SAVE_VIRTUAL_PARAMETERS_FAIL, error);
    }
  }
}
